'use client';

import {Dialog, DialogPanel} from '@headlessui/react';
import { useTranslations } from 'next-intl';
import { ReactNode, forwardRef, useImperativeHandle, useState } from 'react';
import { useBoolean } from 'usehooks-ts';
import { Button } from '@/components/design-system';
import {
  Close as IconClose,
  Location as IconLocation,
} from '@/components/icons';
import { ILocation } from '@/models/location';
import AddressSelect from './AddressSelect';

interface RenderTriggerArgs {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

export interface ForwardedRefs {
  open: () => void;
  close: () => void;
}

interface Props {
  renderTrigger?: (args: RenderTriggerArgs) => ReactNode;
  onChangeAddress: (location: ILocation) => void;
  showSavedAddresses?: boolean;
  showLastAddress?: boolean;
}

const AddressModal = forwardRef<ForwardedRefs, Props>(
  (
    {
      renderTrigger,
      onChangeAddress,
      showSavedAddresses = false,
      showLastAddress = false,
    },
    ref,
  ) => {
    const t = useTranslations('common');
    const { value: isOpen, setTrue: open, setFalse: close } = useBoolean();
    const [q, setQ] = useState('');
    useImperativeHandle(ref, () => ({
      open,
      close,
    }));

    return (
      <>
        {renderTrigger ? renderTrigger({ isOpen, open, close }) : null}
        <Dialog open={isOpen} onClose={close} className="relative z-110">
          {/* The backdrop, rendered as a fixed sibling to the panel container */}
          <div
            className="fixed inset-0 bg-black/30"
            aria-hidden="true"
          />

          {/* Full-screen container to center the panel */}
          <div className="fixed inset-0">
            <DialogPanel
              className={`
              absolute bg-white p-0
              inset-x-0 top-10 bottom-0 sm:inset-x-32 sm:inset-y-auto sm:top-32
              sm:max-w-2xl sm:max-h-[650px] sm:mx-auto
              flex flex-col
              border-t sm:border-none border-greyscale-separator rounded-t-2xl sm:rounded-b-2xl
            `}
            >
              <form
                className="flex items-center px-3 h-14 border-b border-greyscale-separator shrink-0"
                onSubmit={(e) => e.preventDefault()}
              >
                <div className="shrink-0">
                  <IconLocation className="text-greyscale-placeholder" />
                </div>
                <input
                  autoFocus
                  className="flex-1 form-input h-full text-base font-medium tracking-tight text-greyscale-body placeholder:text-greyscale-placeholder border-none focus:ring-transparent"
                  placeholder={t('address.insert')}
                  onChange={(e) => setQ(e.target.value)}
                  value={q}
                />
                <Button
                  className="shrink-0"
                  onClick={(e) => {
                    e.preventDefault();
                    setQ('');
                    close();
                  }}
                  palette="gray"
                  theme="ghost"
                  type="button"
                  variant="icon"
                >
                  <IconClose className="" />
                </Button>
              </form>
              <div className="flex-1 overflow-y-auto">
                <AddressSelect
                  q={q}
                  showSavedAddresses={showSavedAddresses}
                  showLastAddress={showLastAddress}
                  isFocused={true}
                  onChangeQ={setQ}
                  onChangeAddress={(location) => {
                    onChangeAddress(location);
                    close();
                  }}
                />
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      </>
    );
  },
);

export default AddressModal;
